import { createSelector } from 'reselect'
import { getLuxPlusSubscriptionItems } from '../view/luxPlusSubscription'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import { floatify } from 'lib/maths/mathUtils'
import getCheckoutTotals from './getCheckoutTotals'
import { getInsuranceTotals } from './insurance'
import { getBookingProtectionTotals } from './bookingProtection'
import { PAYMENT_OPTIONS, PAYMENT_SCHEDULE_STATUS } from 'constants/payment'
import { getGroupPaymentCreditPayableAmount } from './groupPayment/getGroupPaymentDepositAmount'

const getGiftCardPayableAmount = createSelector(
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getCheckoutTotals(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getBookingProtectionTotals(state),
  (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.GROUP_PAYMENT,
  (state: App.State) => getGroupPaymentCreditPayableAmount(state),
  (
    payable,
    redeemedCards,
    currencyCode,
    luxPlusCartSubscriptionItems,
    cartMode,
    existingOrder,
    checkoutTotals,
    insuranceTotals,
    bookingProtectionTotals,
    isGroupPayment,
    groupPaymentCreditPayableAmount,
  ): number => {
    let maxGiftCardTotal: number

    // Check if this is a change-dates flow with an active payment schedule
    // In this case, gift card should be limited to the "pay now" amount, not the full adjustment
    const hasActivePaymentSchedule = existingOrder?.paymentScheduleDetails &&
      existingOrder.paymentScheduleDetails.status === PAYMENT_SCHEDULE_STATUS.ACTIVE

    let effectivePayable = payable

    if ((cartMode === 'change-dates' || cartMode === 'change-package') && hasActivePaymentSchedule) {
      // Use the paymentScheduleAdjustment (pay now amount) plus any insurance/booking protection
      const paymentScheduleAdjustment = (checkoutTotals.data.paymentScheduleAdjustment ?? 0) +
        (insuranceTotals?.data.price ?? 0) +
        (bookingProtectionTotals?.data.price ?? 0)

      if (paymentScheduleAdjustment > 0) {
        effectivePayable = paymentScheduleAdjustment
      }
    }

    // Group Payment: the deposit less LuxPlus, so LuxPlus is already excluded below.
    const isGroupPaymentPayable = isGroupPayment && groupPaymentCreditPayableAmount > 0
    if (isGroupPaymentPayable) {
      effectivePayable = groupPaymentCreditPayableAmount
    }

    // Calculate total amount of redeemed gift cards for the current currency
    const giftCardBalance = redeemedCards
      .filter(card => card.currencyCode === currencyCode)
      .reduce((total, card) => total + parseFloat(String(card.amount)), 0)

    maxGiftCardTotal = Math.min(giftCardBalance, effectivePayable)

    if (luxPlusCartSubscriptionItems.length > 0 && !isGroupPaymentPayable) {
      const subscriptionItem = luxPlusCartSubscriptionItems[0]
      const maxGiftCardTotalPayableForSubscription = floatify(effectivePayable - subscriptionItem.amount)

      if (maxGiftCardTotal > maxGiftCardTotalPayableForSubscription) {
        maxGiftCardTotal = maxGiftCardTotalPayableForSubscription
      }
    }

    return floatify(maxGiftCardTotal)
  },
)

export default getGiftCardPayableAmount
